import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Upload,
  ArrowRight,
  Shield,
  Users,
  FileCheck,
  Globe,
  Clock,
  Award,
  Building2,
  Scale,
  MapPin,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Linkedin,
  X,
  Briefcase,
  Heart,
  Star,
  AlertCircle,
  GraduationCap,
  Plane,
  FileText,
  UserCheck,
  Handshake,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static data with stable IDs for React keys
const HERO_BENEFITS = [
  { id: "benefit-1", text: "Direct employer connections" },
  { id: "benefit-2", text: "Faster nurse-to-employer matching" },
  { id: "benefit-3", text: "Step-by-step visa guidance" },
];

const PROBLEMS_DATA = [
  {
    id: "problem-1",
    icon: AlertCircle,
    title: "Confusing Immigration Process",
    description: "Complex visa requirements, unclear timelines, and constantly changing regulations make the journey overwhelming.",
  },
  {
    id: "problem-2",
    icon: Shield,
    title: "Unreliable Recruiters",
    description: "Many agencies make false promises, charge hidden fees, and disappear when problems arise.",
  },
  {
    id: "problem-3",
    icon: Users,
    title: "Lack of Employer Connections",
    description: "Without direct access to verified U.S. healthcare employers, finding legitimate opportunities is nearly impossible.",
  },
];

const SOLUTION_POINTS = [
  { id: "solution-1", text: "Direct partnerships with PERM-certified U.S. employers" },
  { id: "solution-2", text: "Experienced immigration coordination support" },
  { id: "solution-3", text: "Transparent process with no hidden fees" },
  { id: "solution-4", text: "Philippine-based support team who understands you" },
];

const PROCESS_STEPS = [
  {
    id: "step-1",
    number: "01",
    icon: FileText,
    title: "Apply",
    subtitle: "Join Talent Pool",
    description: "Submit your application to the PacificEdge Nurse Talent Pool with your credentials.",
  },
  {
    id: "step-2",
    number: "02",
    icon: UserCheck,
    title: "Screen",
    subtitle: "Credential Review",
    description: "Our team reviews your qualifications, NCLEX status, and VisaScreen eligibility.",
  },
  {
    id: "step-3",
    number: "03",
    icon: Handshake,
    title: "Match",
    subtitle: "Employer Connection",
    description: "We connect you with verified U.S. employers ready to sponsor your EB-3 visa.",
  },
  {
    id: "step-4",
    number: "04",
    icon: Plane,
    title: "Relocate",
    subtitle: "Start Your Journey",
    description: "Complete immigration processing and begin your U.S. nursing career.",
  },
];

const BENEFITS_DATA = [
  {
    id: "benefit-card-1",
    icon: Building2,
    title: "Direct U.S. Employer Relationships",
    description: "We maintain direct partnerships with PERM-certified healthcare facilities actively hiring international nurses.",
  },
  {
    id: "benefit-card-2",
    icon: Scale,
    title: "Experienced Immigration Coordination",
    description: "Work with our partner immigration attorneys who specialize in EB-3 nurse petitions.",
  },
  {
    id: "benefit-card-3",
    icon: GraduationCap,
    title: "VisaScreen Preparation Support",
    description: "Guidance through the VisaScreen certification process required for healthcare professionals.",
  },
  {
    id: "benefit-card-4",
    icon: FileCheck,
    title: "Transparent Process Guidance",
    description: "Clear communication at every step with realistic timelines and honest expectations.",
  },
  {
    id: "benefit-card-5",
    icon: Clock,
    title: "Faster Employer Matching",
    description: "Our established network allows for quicker matching with suitable healthcare employers.",
  },
  {
    id: "benefit-card-6",
    icon: Award,
    title: "No Initial Placement Fee",
    description: "We don't charge placement fees upfront. Your success is our priority.",
  },
];

const ELIGIBILITY_REQUIREMENTS = [
  { id: "req-1", icon: GraduationCap, title: "Nursing Degree", description: "BSN or equivalent nursing qualification" },
  { id: "req-2", icon: Award, title: "NCLEX Pass", description: "Passed NCLEX-RN or NCLEX-PN examination" },
  { id: "req-3", icon: FileCheck, title: "VisaScreen Eligibility", description: "English proficiency for VisaScreen certification" },
  { id: "req-4", icon: Plane, title: "Relocation Commitment", description: "Ready to relocate to the United States" },
];

const TRUST_POINTS = [
  {
    id: "trust-1",
    icon: Scale,
    title: "Partner Immigration Law Firm",
    description: "Work with experienced attorneys specializing in EB-3 nurse immigration cases.",
  },
  {
    id: "trust-2",
    icon: Building2,
    title: "U.S. Healthcare Employer Network",
    description: "Direct connections with verified, PERM-certified healthcare facilities across the United States.",
  },
  {
    id: "trust-3",
    icon: MapPin,
    title: "Philippine Recruitment Presence",
    description: "Local support team in the Philippines who understands your journey and speaks your language.",
  },
];

const FAQ_DATA = [
  {
    id: "faq-1",
    question: "How long does the EB-3 visa process take?",
    answer: "The EB-3 visa timeline varies depending on your country of origin and current processing times. Generally, the process can take 2-4 years from PERM filing to visa issuance. We provide realistic timeline guidance based on current conditions and keep you informed throughout the process.",
  },
  {
    id: "faq-2",
    question: "What is VisaScreen?",
    answer: "VisaScreen is a certificate required for healthcare professionals seeking U.S. employment. It verifies your nursing education, licensure, and English proficiency. The certificate is issued by CGFNS (Commission on Graduates of Foreign Nursing Schools) and is mandatory for EB-3 visa processing.",
  },
  {
    id: "faq-3",
    question: "Do I need to pass NCLEX first before applying?",
    answer: "While having an NCLEX pass is ideal, you can join our Talent Pool while preparing for NCLEX. However, passing NCLEX is required before employer matching and visa petition filing. We can provide guidance on NCLEX preparation resources.",
  },
  {
    id: "faq-4",
    question: "Are there upfront fees to apply?",
    answer: "No, there are no placement fees to join our Nurse Talent Pool and go through the initial screening process. We believe in transparent practices and will clearly communicate any costs that may arise during the immigration process, which are typically covered by sponsoring employers.",
  },
  {
    id: "faq-5",
    question: "What types of nursing positions are available?",
    answer: "Our U.S. healthcare partners offer various positions including Medical-Surgical, ICU, Emergency, Operating Room, Labor & Delivery, and Long-Term Care nursing roles. Position availability depends on employer needs and your qualifications.",
  },
  {
    id: "faq-6",
    question: "How is PacificEdge different from other recruitment agencies?",
    answer: "PacificEdge VA maintains direct relationships with verified U.S. employers, provides transparent process guidance, has Philippine-based support, and charges no initial placement fees. We focus on honest communication and realistic expectations throughout your journey.",
  },
];

const FOOTER_LINKS = [
  { id: "link-1", name: "How It Works", href: "#process" },
  { id: "link-2", name: "Eligibility", href: "#eligibility" },
  { id: "link-3", name: "FAQ", href: "#faq" },
  { id: "link-4", name: "Apply Now", href: "#apply" },
];

// Hero Section
const HeroSection = ({ scrollToForm }) => {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2070&auto=format&fit=crop')`,
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/85 to-[#050505]" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-24 text-center">
        <p className="font-accent text-sm tracking-[0.3em] uppercase text-[#D4AF37] mb-6 animate-fade-in">
          PacificEdge Management Solutions
        </p>

        <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.1] mb-8 animate-fade-in-up">
          Your Pathway to a <br />
          <span className="text-gold-gradient">U.S. Nursing Career</span>
          <br />
          Starts Here
        </h1>

        <p className="font-body text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-10 animate-fade-in-up delay-200">
          Connect with verified U.S. healthcare employers through the EB-3
          immigrant visa pathway.
        </p>

        {/* Benefits */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-12 animate-fade-in-up delay-300">
          {HERO_BENEFITS.map((benefit) => (
            <div key={benefit.id} className="flex items-center gap-2 text-gray-200">
              <Check className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm md:text-base">{benefit.text}</span>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <Button
          onClick={scrollToForm}
          data-testid="hero-cta-button"
          className="bg-[#D4AF37] text-black hover:bg-[#B89628] px-10 py-7 text-lg font-semibold tracking-wide transition-all duration-300 hover:scale-105 btn-shine cursor-pointer"
        >
          Apply to the Nurse Talent Pool
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-[#D4AF37]/60" />
        </div>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  return (
    <section
      id="problem"
      className="py-20 md:py-32 bg-[#050505]"
      data-testid="problem-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="font-accent text-sm tracking-[0.25em] uppercase text-[#D4AF37] mb-4">
            The Challenge
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            We Understand Your Struggles
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto">
            Filipino nurses face significant challenges when pursuing U.S.
            employment opportunities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {PROBLEMS_DATA.map((problem) => (
            <div
              key={problem.id}
              className="bg-[#111111] border border-white/10 p-8 card-hover group"
              data-testid={problem.id}
            >
              <div className="w-14 h-14 bg-[#1A1A1A] border border-[#D4AF37]/30 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/10 transition-colors">
                <problem.icon className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-xl text-white mb-4">
                {problem.title}
              </h3>
              <p className="font-body text-gray-400 leading-relaxed">
                {problem.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solution Section
const SolutionSection = () => {
  return (
    <section
      id="solution"
      className="py-20 md:py-32 bg-[#0A0A0A]"
      data-testid="solution-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="relative overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=2080&auto=format&fit=crop"
                alt="Healthcare professionals"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            </div>
            {/* Stats overlay */}
            <div className="absolute bottom-8 left-8 right-8 bg-[#111111]/90 backdrop-blur-sm border border-[#D4AF37]/30 p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-[#D4AF37] text-3xl font-bold font-heading">
                    500+
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    Nurses Placed
                  </p>
                </div>
                <div>
                  <p className="text-[#D4AF37] text-3xl font-bold font-heading">
                    50+
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    U.S. Partners
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <p className="font-accent text-sm tracking-[0.25em] uppercase text-[#D4AF37] mb-4">
              The Solution
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
              Your Trusted Bridge to{" "}
              <span className="text-[#D4AF37]">U.S. Healthcare</span>
            </h2>
            <p className="font-body text-lg text-gray-400 mb-8 leading-relaxed">
              PacificEdge VA serves as the trusted bridge between qualified
              Filipino nurses and verified U.S. healthcare employers. We
              streamline the entire process—from credential review to employer
              matching to immigration coordination.
            </p>

            <div className="space-y-4">
              {SOLUTION_POINTS.map((item) => (
                <div key={item.id} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#D4AF37] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-black" />
                  </div>
                  <p className="text-gray-300">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Process Section (EB-3 Pathway)
const ProcessSection = ({ scrollToForm }) => {
  return (
    <section
      id="process"
      className="py-20 md:py-32 bg-[#050505]"
      data-testid="process-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="font-accent text-sm tracking-[0.25em] uppercase text-[#D4AF37] mb-4">
            The Process
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            How the EB-3 Pathway Works
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto">
            A clear, structured path from application to U.S. employment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connector line for desktop */}
          <div className="hidden lg:block absolute top-20 left-[12%] right-[12%] h-[2px] bg-gradient-to-r from-[#D4AF37] via-[#D4AF37]/50 to-[#D4AF37]" />

          {PROCESS_STEPS.map((step) => (
            <div key={step.id} className="relative" data-testid={step.id}>
              <div className="bg-[#111111] border border-white/10 p-8 h-full card-hover group relative z-10">
                {/* Step number */}
                <div className="w-16 h-16 bg-[#050505] border-2 border-[#D4AF37] flex items-center justify-center mb-6 mx-auto group-hover:bg-[#D4AF37]/10 transition-colors">
                  <step.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>

                <p className="font-accent text-[#D4AF37] text-sm tracking-wider mb-2 text-center">
                  STEP {step.number}
                </p>
                <h3 className="font-heading text-xl text-white mb-1 text-center">
                  {step.title}
                </h3>
                <p className="text-[#D4AF37]/70 text-sm mb-4 text-center">
                  {step.subtitle}
                </p>
                <p className="font-body text-gray-400 text-center text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button
            onClick={scrollToForm}
            data-testid="process-cta-button"
            className="bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-10 py-6 text-lg font-semibold tracking-wide transition-all duration-300 cursor-pointer"
          >
            Start Your Journey Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  return (
    <section
      id="benefits"
      className="py-20 md:py-32 bg-[#0A0A0A]"
      data-testid="benefits-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="font-accent text-sm tracking-[0.25em] uppercase text-[#D4AF37] mb-4">
            Why Choose Us
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            Why Nurses Choose PacificEdge
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto">
            Experience the difference of working with a team that truly cares
            about your success.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {BENEFITS_DATA.map((benefit) => (
            <div
              key={benefit.id}
              className="benefit-card"
              data-testid={benefit.id}
            >
              <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-5">
                <benefit.icon className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-xl text-white mb-3">
                {benefit.title}
              </h3>
              <p className="font-body text-gray-400 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Eligibility Section
const EligibilitySection = ({ scrollToForm }) => {
  return (
    <section
      id="eligibility"
      className="py-20 md:py-32 bg-[#050505]"
      data-testid="eligibility-section"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="font-accent text-sm tracking-[0.25em] uppercase text-[#D4AF37] mb-4">
            Requirements
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            Eligibility Checklist
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto">
            Check if you meet the minimum requirements to join our Nurse Talent
            Pool.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {ELIGIBILITY_REQUIREMENTS.map((req) => (
            <div
              key={req.id}
              className="checklist-item"
              data-testid={req.id}
            >
              <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <req.icon className="w-5 h-5 text-black" />
              </div>
              <div>
                <h4 className="font-heading text-lg text-white">{req.title}</h4>
                <p className="text-gray-400 text-sm">{req.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Button
            onClick={scrollToForm}
            data-testid="eligibility-cta-button"
            className="bg-[#D4AF37] text-black hover:bg-[#B89628] px-10 py-6 text-lg font-semibold tracking-wide transition-all duration-300 hover:scale-105 cursor-pointer"
          >
            Apply Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Trust Section
const TrustSection = () => {
  return (
    <section
      id="trust"
      className="py-20 md:py-32 bg-[#0A0A0A]"
      data-testid="trust-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="font-accent text-sm tracking-[0.25em] uppercase text-[#D4AF37] mb-4">
            Trust & Credibility
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            Built on Trust & Transparency
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto">
            Your success is backed by our established network and proven track
            record.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {TRUST_POINTS.map((point) => (
            <div
              key={point.id}
              className="bg-[#111111] border border-white/10 p-8 text-center card-hover"
              data-testid={point.id}
            >
              <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6">
                <point.icon className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-xl text-white mb-4">
                {point.title}
              </h3>
              <p className="font-body text-gray-400 leading-relaxed">
                {point.description}
              </p>
            </div>
          ))}
        </div>

        {/* Trust badge */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 bg-[#111111] border border-[#D4AF37]/30 px-6 py-3">
            <Shield className="w-6 h-6 text-[#D4AF37]" />
            <span className="text-gray-300">
              Structured EB-3 Process Guidance
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  return (
    <section
      id="faq"
      className="py-20 md:py-32 bg-[#050505]"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="font-accent text-sm tracking-[0.25em] uppercase text-[#D4AF37] mb-4">
            FAQ
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            Frequently Asked Questions
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto">
            Get answers to common questions about the EB-3 pathway and our
            services.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {FAQ_DATA.map((faq) => (
            <AccordionItem
              key={faq.id}
              value={faq.id}
              className="bg-[#111111] border border-white/10 px-6"
              data-testid={faq.id}
            >
              <AccordionTrigger className="font-heading text-lg text-white hover:text-[#D4AF37] py-6 text-left">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="font-body text-gray-400 pb-6 leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Application Form Section
const ApplicationFormSection = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    city_province: "",
    nursing_license: "",
    nclex_status: "",
    years_experience: "",
  });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["pdf", "doc", "docx"].includes(ext)) {
        toast.error("Please upload a PDF, DOC, or DOCX file");
        return;
      }
      setResume(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["pdf", "doc", "docx"].includes(ext)) {
        toast.error("Please upload a PDF, DOC, or DOCX file");
        return;
      }
      setResume(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formPayload = new FormData();
      Object.keys(formData).forEach((key) => {
        formPayload.append(key, formData[key]);
      });
      if (resume) {
        formPayload.append("resume", resume);
      }

      const response = await axios.post(`${API}/applications`, formPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(
        "Application submitted successfully! We will contact you soon."
      );
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        city_province: "",
        nursing_license: "",
        nclex_status: "",
        years_experience: "",
      });
      setResume(null);
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="apply"
      className="py-20 md:py-32 bg-[#0A0A0A]"
      data-testid="application-form-section"
    >
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-accent text-sm tracking-[0.25em] uppercase text-[#D4AF37] mb-4">
            Apply Now
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            Start Your U.S. Nursing Career Journey
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto">
            Join our Nurse Talent Pool and take the first step toward your
            American dream.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#111111] border border-white/10 p-8 md:p-12 gold-glow"
          data-testid="application-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-gray-300">
                Full Name <span className="text-[#D4AF37]">*</span>
              </Label>
              <Input
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                placeholder="Juan Dela Cruz"
                className="form-input bg-[#1A1A1A] border-white/10 text-white h-14"
                data-testid="input-full-name"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">
                Email <span className="text-[#D4AF37]">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="juan@email.com"
                className="form-input bg-[#1A1A1A] border-white/10 text-white h-14"
                data-testid="input-email"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-300">
                Phone / WhatsApp <span className="text-[#D4AF37]">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="+63 912 345 6789"
                className="form-input bg-[#1A1A1A] border-white/10 text-white h-14"
                data-testid="input-phone"
              />
            </div>

            {/* City/Province */}
            <div className="space-y-2">
              <Label htmlFor="city_province" className="text-gray-300">
                City / Province <span className="text-[#D4AF37]">*</span>
              </Label>
              <Input
                id="city_province"
                name="city_province"
                value={formData.city_province}
                onChange={handleInputChange}
                required
                placeholder="Manila, Metro Manila"
                className="form-input bg-[#1A1A1A] border-white/10 text-white h-14"
                data-testid="input-city-province"
              />
            </div>

            {/* Nursing License */}
            <div className="space-y-2">
              <Label htmlFor="nursing_license" className="text-gray-300">
                Nursing License <span className="text-[#D4AF37]">*</span>
              </Label>
              <Select
                value={formData.nursing_license}
                onValueChange={(value) =>
                  handleSelectChange("nursing_license", value)
                }
              >
                <SelectTrigger
                  className="form-input bg-[#1A1A1A] border-white/10 text-white h-14"
                  data-testid="select-nursing-license"
                >
                  <SelectValue placeholder="Select license type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="rn">Registered Nurse (RN)</SelectItem>
                  <SelectItem value="lpn">
                    Licensed Practical Nurse (LPN/LVN)
                  </SelectItem>
                  <SelectItem value="bsn">BSN Graduate</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* NCLEX Status */}
            <div className="space-y-2">
              <Label htmlFor="nclex_status" className="text-gray-300">
                NCLEX Status <span className="text-[#D4AF37]">*</span>
              </Label>
              <Select
                value={formData.nclex_status}
                onValueChange={(value) =>
                  handleSelectChange("nclex_status", value)
                }
              >
                <SelectTrigger
                  className="form-input bg-[#1A1A1A] border-white/10 text-white h-14"
                  data-testid="select-nclex-status"
                >
                  <SelectValue placeholder="Select NCLEX status" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="scheduled">
                    Scheduled to take exam
                  </SelectItem>
                  <SelectItem value="preparing">Currently preparing</SelectItem>
                  <SelectItem value="not-yet">Not yet started</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Years of Experience */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="years_experience" className="text-gray-300">
                Years of Nursing Experience{" "}
                <span className="text-[#D4AF37]">*</span>
              </Label>
              <Select
                value={formData.years_experience}
                onValueChange={(value) =>
                  handleSelectChange("years_experience", value)
                }
              >
                <SelectTrigger
                  className="form-input bg-[#1A1A1A] border-white/10 text-white h-14"
                  data-testid="select-years-experience"
                >
                  <SelectValue placeholder="Select experience" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="0-1">Less than 1 year</SelectItem>
                  <SelectItem value="1-3">1-3 years</SelectItem>
                  <SelectItem value="3-5">3-5 years</SelectItem>
                  <SelectItem value="5-10">5-10 years</SelectItem>
                  <SelectItem value="10+">10+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Resume Upload */}
            <div className="space-y-2 md:col-span-2">
              <Label className="text-gray-300">Upload Resume (Optional)</Label>
              <div
                className={`file-upload-area p-8 text-center cursor-pointer ${
                  dragOver ? "dragover" : ""
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                data-testid="resume-upload-area"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="input-resume"
                />
                {resume ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileCheck className="w-8 h-8 text-[#D4AF37]" />
                    <div>
                      <p className="text-white font-medium">{resume.name}</p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setResume(null);
                        }}
                        className="text-sm text-gray-400 hover:text-[#D4AF37] mt-1"
                      >
                        Remove file
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-[#D4AF37]/60 mx-auto mb-3" />
                    <p className="text-gray-400 mb-1">
                      Drag & drop your resume or{" "}
                      <span className="text-[#D4AF37]">browse</span>
                    </p>
                    <p className="text-gray-500 text-sm">
                      PDF, DOC, DOCX (Max 5MB)
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#B89628] py-7 text-lg font-semibold tracking-wide transition-all duration-300 disabled:opacity-50 cursor-pointer"
              data-testid="submit-application-button"
            >
              {isSubmitting ? (
                "Submitting..."
              ) : (
                <>
                  Apply to the Nurse Talent Pool
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </div>

          <p className="text-gray-500 text-sm text-center mt-6">
            By submitting, you agree to our Privacy Policy and consent to be
            contacted regarding your application.
          </p>
        </form>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Successfully subscribed to updates!");
      setEmail("");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-[#050505] border-t border-white/10 py-16" data-testid="footer">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <h3 className="font-heading text-2xl text-white mb-4">
              Pacific<span className="text-[#D4AF37]">Edge</span> Management Solutions
            </h3>
            <p className="text-gray-400 mb-6 max-w-md">
              Connecting qualified Filipino nurses with verified U.S. healthcare
              employers through the EB-3 immigrant visa pathway.
            </p>

            {/* Newsletter */}
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="form-input bg-[#1A1A1A] border-white/10 text-white flex-1"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={subscribing}
                className="bg-[#D4AF37] text-black hover:bg-[#B89628] px-6 cursor-pointer"
                data-testid="newsletter-submit-button"
              >
                {subscribing ? "..." : "Subscribe"}
              </Button>
            </form>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg text-white mb-4">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {FOOTER_LINKS.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="font-heading text-lg text-white mb-4">Connect</h4>
            <div className="flex gap-4 mb-6">
              <a
                href="https://www.facebook.com/pacificedgeva/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-gray-400"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-gray-400"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-gray-400"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
            <p className="text-gray-500 text-sm">
              <Mail className="w-4 h-4 inline mr-2" />
              careers@pacificedgeva.com
            </p>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © 2026 PacificEdge VA. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a
              href="#"
              className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="#"
              className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Sticky CTA Component
const StickyCTA = ({ visible, scrollToForm }) => {
  return (
    <div
      className={`sticky-cta ${visible ? "visible" : ""}`}
      data-testid="sticky-cta"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <p className="hidden sm:block text-white font-medium">
          Ready to start your U.S. nursing career?
        </p>
        <Button
          onClick={scrollToForm}
          className="bg-[#D4AF37] text-black hover:bg-[#B89628] px-8 py-5 font-semibold w-full sm:w-auto cursor-pointer"
          data-testid="sticky-cta-button"
        >
          Apply Now
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  const scrollToForm = useCallback(() => {
    const formSection = document.getElementById("apply");
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = window.innerHeight;
      const pageHeight = document.body.scrollHeight;
      const windowHeight = window.innerHeight;

      // Show sticky CTA after scrolling past hero, hide near footer
      setShowStickyCTA(scrollY > heroHeight * 0.8 && scrollY < pageHeight - windowHeight * 2);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="landing-page">
      <HeroSection scrollToForm={scrollToForm} />
      <ProblemSection />
      <SolutionSection />
      <ProcessSection scrollToForm={scrollToForm} />
      <BenefitsSection />
      <EligibilitySection scrollToForm={scrollToForm} />
      <TrustSection />
      <FAQSection />
      <ApplicationFormSection />
      <Footer />
      <StickyCTA visible={showStickyCTA} scrollToForm={scrollToForm} />
    </main>
  );
};

export default LandingPage;
